import { Cart, CartLineEdgeNode } from 'shopify-buy';
import { useEffect, useState } from 'preact/hooks';

import { MinusIcon } from './icons/MinusIcon';
import { PlusIcon } from './icons/PlusIcon';
import React from 'preact/compat';
import { TrashIcon } from './icons/TrashIcon';
import { notifyHandledException } from '../../utils/errorLogger';
import { updateItem } from './Cart';
import { useDebounced } from './hooks/useDebounced';

interface QuantityProps {
  line: CartLineEdgeNode | null;
  onChange: (cart: Cart | undefined) => void;
}

export const IS_QUANTITY_LIMITS_ENABLED = true;

const useQuantityLimits = (
  line: CartLineEdgeNode | null,
  quantity: number
): number => {
  if (!IS_QUANTITY_LIMITS_ENABLED) {
    return quantity;
  }

  const min = line?.attributes?.find((attr) => attr.key === '_qty_min')?.value;
  const max = line?.attributes?.find((attr) => attr.key === '_qty_max')?.value;

  if (!min || !max) {
    return quantity;
  }

  const qty = Math.min(Math.max(quantity, parseInt(min)), parseInt(max));

  if (isNaN(qty)) {
    return quantity;
  }

  return qty;
};

export const Quantity: React.FC<QuantityProps> = ({
  line = null,
  onChange = () => null,
}) => {
  const [quantity, setQuantity] = useState<string | number>(
    line?.quantity || 1
  );
  const debouncedQuantity = useDebounced(quantity, 200);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const update = (): void => {
    if (line) {
      document.body.classList.add('cart--busy');
      setIsBusy(true);
      updateItem(line.id, quantity)
        .then((checkout) => {
          onChange(checkout);
          document.body.classList.remove('cart--busy');
          setIsBusy(false);
        })
        .catch(notifyHandledException);
    }
  };
  const increment = (): void => {
    setConfirmRemoval(false);
    const nextNumber = parseInt(String(quantity)) + 1;
    setQuantity(useQuantityLimits(line, nextNumber));
  };

  const decrement = (): void => {
    setQuantity(useQuantityLimits(line, parseInt(String(quantity)) - 1));
  };

  useEffect(update, [debouncedQuantity]);

  useEffect(() => {
    if (isBusy) {
      return;
    }

    setQuantity(useQuantityLimits(line, line?.quantity || 1));
  }, [line?.quantity]);

  return (
    <div className={'quantity-selector__input'}>
      {confirmRemoval ? (
        <button
          disabled={isBusy}
          onClick={() => setQuantity(0)}
          aria-label={`Remove ${line?.merchandise.title} from cart`}
        >
          <div class={'flex'}>
            <TrashIcon
              title={`remove ${line?.merchandise.title} from your cart`}
            />
          </div>
        </button>
      ) : (
        <button
          disabled={isBusy}
          onClick={decrement}
          aria-label={`Decrement quantity of ${line?.merchandise.title}`}
        >
          <div>
            <MinusIcon
              title={`decrement quantity of ${line?.merchandise.title}`}
            />
          </div>
        </button>
      )}
      <input
        aria-label={`Quantity for ${line?.merchandise.title}`}
        type={'number'}
        min={1}
        disabled={isBusy}
        value={quantity}
        data-value={quantity}
        onChange={(e) => {
          if (isBusy) {
            return;
          }

          const input = e.target;
          if (input instanceof HTMLInputElement) {
            if (!input.value) {
              return;
            }

            setQuantity(useQuantityLimits(line, parseInt(input.value)));
          }
        }}
      />
      <button
        disabled={isBusy}
        onClick={increment}
        aria-label={`Increment quantity of ${line?.merchandise.title}`}
      >
        <div>
          <PlusIcon
            title={`increment quantity of ${line?.merchandise.title}`}
          />
        </div>
      </button>
    </div>
  );
};
